import { Segment } from '@dna/analytics'
import { RespondToViewport, WithProfiler } from '@dna/mixins'
import {
  chevronLeftAlternate1,
  chevronRightAlternate1,
  minus,
  plus,
  rothysLogo,
} from '@dna/paths'
import { storefrontClient } from '@dna/storefront'
import styles from '@dna/styles'
import { handleize, t } from '@dna/utils'
import { LitElement, classMap, css, html, nothing, repeat, when } from '@lit'

class DNAStoreIndex extends WithProfiler(RespondToViewport(LitElement)) {
  static properties = {
    locations: {
      type: Array,
      state: true,
    },
  }

  get locationsUS() {
    const storesUS = this.locations.filter(
      (location) =>
        location.fields.country === 'US' && location.type === 'stores',
    )

    const stateSortedStoresUS = storesUS.sort((a, b) => {
      const aState = a.fields.state.toLowerCase().trim()
      const bState = b.fields.state.toLowerCase().trim()
      if (aState.toLowerCase().trim() < bState.toLowerCase().trim()) {
        return -1
      }
    })

    const citySortedStoresUS = stateSortedStoresUS.sort((a, b) => {
      const aState = a.fields.state.toLowerCase().trim()
      const bState = b.fields.state.toLowerCase().trim()
      const aCity = a.fields.city.toLowerCase().trim()
      const bCity = b.fields.city.toLowerCase().trim()

      if (aState === bState && aCity < bCity) {
        return -1
      }
    })

    return [
      ...citySortedStoresUS.filter((store) => store.fields.outlet === 'false'),
      ...citySortedStoresUS.filter((store) => store.fields.outlet === 'true'),
    ]
  }

  get locationsIntl() {
    const storesIntl = this.locations.filter(
      (location) =>
        location.fields.country !== 'US' && location.type === 'stores',
    )

    const countrySortedStoresIntl = storesIntl.sort((a, b) => {
      const aCountry = a.fields.country.toLowerCase().trim()
      const bCountry = b.fields.country.toLowerCase().trim()
      const aCity = a.fields.city.toLowerCase().trim()
      const bCity = b.fields.city.toLowerCase().trim()

      if (aCountry < bCountry) {
        return -1
      }
    })

    const citySortedStoresIntl = countrySortedStoresIntl.sort((a, b) => {
      const aCountry = a.fields.country.toLowerCase().trim()
      const bCountry = b.fields.country.toLowerCase().trim()
      const aCity = a.fields.city.toLowerCase().trim()
      const bCity = b.fields.city.toLowerCase().trim()

      if (aCountry === bCountry && aCity < bCity) {
        return -1
      }
    })

    return [
      ...citySortedStoresIntl.filter(
        (store) => store.fields.outlet === 'false',
      ),
      ...citySortedStoresIntl.filter((store) => store.fields.outlet === 'true'),
    ]
  }

  get regionsUS() {
    const regionsUS = this.locationsUS.map((location) => {
      if (location.fields.region) return location.fields.region
    })
    const uniqueRegionsUS = [...new Set(regionsUS)]
    return uniqueRegionsUS.sort().reverse()
  }

  get regionsIntl() {
    const regionsIntl = this.locationsIntl.map((location) => {
      if (location.fields.region) return location.fields.region
    })
    const uniqueRegionsIntl = [...new Set(regionsIntl)]
    return uniqueRegionsIntl.sort()
  }

  constructor() {
    super()

    this._setMobileBreakpoints(['mobile', 'tablet'])
    this.locations = []
    ;(async () => {
      try {
        const [storeLocations, wholesaleLocations] = await Promise.all([
          storefrontClient.GetAllMetaobjectsByType('stores'),
          storefrontClient.GetAllMetaobjectsByType('wholesale_stores'),
        ])

        document.dispatchEvent(
          new CustomEvent('dna:stores:ready', {
            detail: {
              locations: storeLocations,
              wholesale_locations: wholesaleLocations,
            },
          }),
        )
      } catch (error) {
        console.warn('There was a problem fetching stores.')
        console.log(error.message)
      }
    })()
  }

  connectedCallback() {
    super.connectedCallback()

    document.addEventListener('dna:stores:ready', this._handleStoresReady)
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    document.removeEventListener('dna:stores:ready', this._handleStoresReady)
  }

  _handleStoresReady = (event) => {
    if (
      event.detail?.locations.length &&
      event.detail?.wholesale_locations.length
    ) {
      this.locations = [
        ...event.detail.locations,
        ...event.detail.wholesale_locations,
      ]
    } else if (event.detail?.locations.length) {
      this.locations = event.detail.locations
    }
  }

  _handleTabClick = (event) => {
    const $clickedTab = event.target.querySelector(
      `sl-tab[panel="${event.detail.name}"]`,
    )
    const $clickedPanel = event.target.querySelector(
      `sl-tab-panel[name="${event.detail.name}"]`,
    )

    if ($clickedTab.getAttribute('panel') === 'united-states') {
      if (!this.regionsUS.length) {
        this._setActiveSliderNavVisibility(
          $clickedPanel.querySelector('.js-stores-list-slider'),
        )
      }
    } else {
      if (!this.regionsIntl.length) {
        this._setActiveSliderNavVisibility(
          $clickedPanel.querySelector('.js-stores-list-slider'),
        )
      }
    }

    this._trackRegionTab($clickedTab)
  }

  _handleAccordionOpen = (event) => {
    this._setActiveSliderNavVisibility(
      event.target?.querySelector('.js-stores-list-slider'),
    )
    this._trackRegionAccordion(event)
  }

  _handleAccordionClose = (event) => {
    const $accordion = event.target.closest('.js-stores-list-accordion')
    const $slider = $accordion?.querySelector('.js-stores-list-slider')
    $slider.removeEventListener(
      'scrollend',
      this._setActiveSliderNavVisibility($slider),
    )
  }

  _handleSliderNavClick = (event) => {
    const $clickedNav = event.target.closest('button[class*="arrow"]')

    if (this.$activeSlider) {
      if ($clickedNav.classList.contains('js-stores-list-slider-prev')) {
        if (this.$activeSlider.scrollLeft > 0) {
          this.$activeSlider.scrollBy({
            left: (this.$activeSlider.clientWidth / 2) * -1,
            behavior: 'smooth',
          })
        }
        this._setActiveSliderNavVisibility()
      }
      if ($clickedNav.classList.contains('js-stores-list-slider-next')) {
        if (this.$activeSlider.scrollWidth >= 0) {
          this.$activeSlider.scrollBy({
            left: this.$activeSlider.clientWidth / 2,
            behavior: 'smooth',
          })
        }
        this._setActiveSliderNavVisibility()
      }
    }
  }

  _setActiveSliderNavVisibility = ($slider) => {
    if ($slider) {
      if (this.$activeSlider) {
        this.$activeSlider.removeEventListener('scrollend', (event) => {
          this._setActiveSliderNavVisibility()
        })
      }
      this.$activeSlider = $slider
      const $activeAccordion = this.$activeSlider.closest(
        '.js-stores-list-accordion',
      )
      this.$activeNext = $activeAccordion
        ? $activeAccordion.querySelector('.js-stores-list-slider-next')
        : this.$activeSlider.querySelector('.js-stores-list-slider-next')
      this.$activePrev = $activeAccordion
        ? $activeAccordion.querySelector('.js-stores-list-slider-prev')
        : this.$activeSlider.querySelector('.js-stores-list-slider-prev')

      this.$activeSlider.addEventListener('scrollend', (event) => {
        this._setActiveSliderNavVisibility()
      })
    }

    if (this.$activeSlider?.scrollWidth >= this.$activeSlider?.clientWidth) {
      if (this.$activeSlider.scrollLeft === 0) {
        this.$activeNext.disabled = false
        this.$activePrev.disabled = true
      } else if (
        Math.ceil(this.$activeSlider?.scrollLeft) >=
        this.$activeSlider?.scrollWidth - this.$activeSlider?.clientWidth
      ) {
        this.$activeNext.disabled = true
        this.$activePrev.disabled = false
      } else {
        this.$activeNext.disabled = false
        this.$activePrev.disabled = false
      }
    }
  }

  _trackRegionAccordion = (event) => {
    // @segment
    Segment.protect(() => {
      Segment.track(Segment.CONSTANTS.EVENTS.STORES_REGION_CLICKED, {
        store_region: event.target.getAttribute('summary')?.trim(),
      })
    })
  }

  _trackRegionTab = ($tab) => {
    if ($tab?.active) {
      // @segment
      Segment.protect(() => {
        Segment.track(Segment.CONSTANTS.EVENTS.STORES_REGION_CLICKED, {
          store_region: $tab.textContent.trim(),
        })
      })
    }
  }

  _trackStoreClick = (event) => {
    const $storeCard = event.target.closest('.js-store-link')
    if ($storeCard) {
      // @segment
      Segment.protect(() => {
        Segment.track(Segment.CONSTANTS.EVENTS.STORE_CLICKED, {
          store_name: $storeCard.querySelector('h3')?.textContent?.trim(),
          store_url: $storeCard.href,
          stores_context: 'index list',
        })
      })
    }
  }

  _renderStoreCard(location) {
    return html`
      <div class="stores-index__store-image">
        ${when(
          location.fields.store_locator_image?.url,
          () => html`
            <dna-image
              src="${location.fields.store_locator_image.url}"
              loading="lazy"
              fetch-priority="low"
              fit="cover"
              width="100%"
              height="100%"
              sizes="
                (max-width: 768px) 400px,
                (min-width: 768px) 800px
              "
            /></dna-image>
          `,
          () => html`
            <div class="bg-sandstone flex flex-align-center flex-justify-center w100 h100">
              <div class="c-white">
                ${rothysLogo}
              </div>
            </div>
          `,
        )}
      </div>
      <p class="label-s uppercase">${location.fields.city}, ${location.fields.state}</p>
      <h3 class="h6 mt-2">
        ${location.fields.name}
      </h3>
      <p class="p3">${location.fields.street_address}</p>
    `
  }

  _renderStoresIndex(regions, locations) {
    return html`
      ${when(
        regions.length > 0 && regions.length !== 1 && regions[0],
        () =>
          repeat(
            regions,
            (region) => `accordion-${region}`,
            (region, index) => html`
              <sl-details
                class="stores-index__region p2 js-stores-list-accordion"
                summary="${region}"
                @sl-show=${this._handleAccordionOpen}
              >
                <span slot="expand-icon" class="expand-collapse-icon">
                  ${plus}
                </span>
                <span slot="collapse-icon" class="expand-collapse-icon">
                  ${minus}
                </span>
                <content class="stores-index__list flex js-stores-list-slider">
                  ${repeat(
                    locations.filter(
                      (location) => location.fields.region === region,
                    ),
                    (location) => location.handle,
                    (location) => html`
                      <div
                        class="stores-index__store"
                        tabindex="0"
                      >
                        ${when(
                          location.onlineStoreUrl,
                          () => html`
                            <a
                              href="${location.onlineStoreUrl}"
                              class="js-store-link"
                              @click=${this._trackStoreClick}
                            >
                              ${this._renderStoreCard(location)}
                            </a>
                          `,
                          () => html`
                            <div
                              class="js-store-link"
                              @click=${this._trackStoreClick}
                            >
                              ${this._renderStoreCard(location)}
                            </div>
                          `,
                        )}
                      </div>
                    `,
                  )}
                </content>
                <button
                  class="stores-index__arrow stores-index__arrow--previous js-stores-list-slider-prev"
                  @click=${this._handleSliderNavClick}
                  disabled
                >
                  ${chevronLeftAlternate1}
                </button>
                <button
                  class="stores-index__arrow stores-index__arrow--next js-stores-list-slider-next"
                  @click=${this._handleSliderNavClick}
                  disabled
                >
                  ${chevronRightAlternate1}
                </button>
              </sl-details>
            `,
          ),
        () => html`
          <div class="stores-index__list flex js-stores-list-slider">
            ${repeat(
              locations,
              (location) => location.handle,
              (location, index) => html`
                <div
                  class="stores-index__store"
                  tabindex="0"
                >
                  <a href="${location.onlineStoreUrl}" class="js-store-link">
                    <div class="stores-index__store-image">
                      <dna-image
                        src="${location.fields.store_locator_image.url}"
                        loading="lazy"
                        fetch-priority="low"
                        fit="cover"
                        width="100%"
                        height="100%"
                        sizes="
                          (max-width: 768px) 400px,
                          (min-width: 768px) 800px
                        "
                      ></dna-image>
                    </div>
                    <p class="label-s uppercase">${location.fields.city}, ${location.fields.state}</p>
                    <h3 class="h6 mt-2">
                      ${location.fields.name}
                    </h3>
                    <p class="p1">${location.fields.street_address}</p>
                  </a>
                </div>
              `,
            )}
            <button
              class="stores-index__arrow stores-index__arrow--previous js-stores-list-slider-prev"
              @click=${this._handleSliderNavClick}
              disabled
            >
              ${chevronLeftAlternate1}
            </button>
            <button
              class="stores-index__arrow stores-index__arrow--next js-stores-list-slider-next"
              @click=${this._handleSliderNavClick}
              disabled
            >
              ${chevronRightAlternate1}
            </button>
          </div>
        `,
      )}
    `
  }

  render() {
    return html`
      <sl-tab-group
        class="stores-index__country-selector js-store-list"
        @sl-tab-show=${this._handleTabClick}
      >
        ${when(
          this.locationsUS.length,
          () => html`
            <sl-tab
              class="stores-index__country-selector-tab"
              slot="nav"
              panel=${handleize(t('templates.stores.list.tabs.tabs__1'))}
              ?active=${this.locationsUS.length}
            >
              ${t('templates.stores.list.tabs.tabs__1')}
            </sl-tab>
            <sl-tab-panel
              class="stores-index__country-selector-panel"
              name=${handleize(t('templates.stores.list.tabs.tabs__1'))}
            >
              <div class="stores-index__country-selector-panel-content">
                ${this._renderStoresIndex(this.regionsUS, this.locationsUS)}
              </div>
            </sl-tab-panel>
          `,
          () => nothing,
        )}
        ${when(
          this.locationsIntl.length,
          () => html`
            <sl-tab
              class="stores-index__country-selector-tab"
              slot="nav"
              panel=${handleize(t('templates.stores.list.tabs.tabs__2'))}
              ?active=${!this.locationsUS.length && this.locationsIntl.length}
            >
              ${t('templates.stores.list.tabs.tabs__2')}
            </sl-tab>
            <sl-tab-panel
              class="stores-index__country-selector-panel"
              name=${handleize(t('templates.stores.list.tabs.tabs__2'))}
            >
              <div class="stores-index__country-selector-panel-content">
                ${this._renderStoresIndex(this.regionsIntl, this.locationsIntl)}
              </div>
            </sl-tab-panel>
          `,
          () => nothing,
        )}
        
      </sl-tab-group>
    `
  }

  static styles = [
    styles.a11y,
    styles.color,
    styles.flex,
    styles.reset,
    styles.shoelace,
    styles.sizing,
    styles.spacing,
    styles.typography,
    css`
      :host[data-ready] {
        min-height: unset !important;
      }
      .stores-index__country-selector::part(nav) {
        border-bottom: 1px solid var(--dna-color-light-grey);
        margin-bottom: calc(var(--dna-space-6) + var(--dna-space-2));
        margin-right: var(--dna-space-4);
      }
      .stores-index__country-selector::part(tabs) {
        border: none;
        gap: calc(var(--dna-space-10) + var(--dna-space-4));
      }
      .stores-index__country-selector-tab::part(base) {
        font: var(--dna-label-s-font);
        letter-spacing: var(--dna-space-0-5);
        padding: 0 0 var(--dna-space-3);
        text-transform: uppercase;
      }
      .stores-index__country-selector-panel::part(base) {
        padding: 0;
      }
      .stores-index__region::part(base) {
        border: none;
        background-color: unset; 
        position: relative;
      }
      .stores-index__country-selector-panel-content {
        position: relative;
      }
      .stores-index__region::part(header) {
        margin-right: var(--dna-space-4);
        padding: var(--dna-space-6) 0 var(--dna-space-6);
      }
      .stores-index__region:not(:first-child)::part(header) {
        border-top: 1px solid var(--dna-color-light-grey);
      }
      .stores-index__region:first-child::part(header) {
        padding-top: var(--dna-space-2);
      }
      .stores-index__region::part(content) {
        padding: 0;
      }
      .stores-index__region::part(summary) {
        font: var(--dna-label-s-font);
        letter-spacing: var(--dna-space-0-5);
        text-transform: uppercase;
      }
      .stores-index__region::part(summary-icon) {
        rotate: none;
      }
      .stores-index__region .expand-collapse-icon{
        color: var(--dna-brand-primary);
      }
      .stores-index__list {
        gap: var(--dna-space-2);
        overflow-x: auto;
        padding: 0 0 var(--dna-space-6);
        scroll-snap-type: x mandatory;
      }
      .stores-index__list::part(base) {
        grid-template-areas: 'slides' 'navigation';
        grid-template-columns: 1fr;
        gap: 0;
      }
      .stores-index__arrow {
        display: none;
      }
      .stores-index__store {
        flex: 0 0 202px;
        scroll-snap-align: start;

        h3 {
          font-size: calc(var(--dna-space-4) + var(--dna-space-0-5));
          line-height: calc(var(--dna-space-6) + var(--dna-space-0-5));
          margin-bottom: 0;
        }
        p {
          line-height: calc(var(--dna-space-3) + var(--dna-space-0-5));
          margin: 0;
        }
        p.label-s {
          font-size: calc(var(--dna-space-5) / 2);
          line-height: calc(var(--dna-space-5) / 2);
        }
      }
      .stores-index__store-image {
        aspect-ratio: 1;
        margin-bottom: 24px;
        position: relative;

        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        p {
          background-color: var(--dna-color-reflex-blue);
          color: var(--dna-brand-reverse);
          padding: var(--dna-space-1);
          position: absolute;
          right: var(--dna-space-3);
          bottom: var(--dna-space-3);
        }
      }
      .template-page\.stores-index [id*="__generic_text"] .container {
        max-width: unset;
        padding: 0 var(--dna-space-4);
      }

      @media screen and (min-width: 540px) {
        .stores-index {
          margin-bottom: calc(var(--dna-space-10) + var(--dna-space-5));
        }
        .stores-index__header {
          margin-bottom: var(--dna-space-10);

          h2 {
            font: var(--dna-h2-font);
          }
        }
        .stores-index__region:first-child::part(header) {
          padding-top: var(--dna-space-6);
        }
        .stores-index__region::part(summary) {
          font: var(--dna-label-m-font);
        }
        .stores-index__list {
          padding: var(--dna-space-2) 0 var(--dna-space-10);
        }
        .stores-index__store {
          h3 {
            font: var(--dna-h4-font);
          }
          p {
            font: var(--dna-body-p1-font);
          }
          p.label-s {
            font: var(--dna-label-m-font);
          }
        }
      }

      @media screen and (min-width: 768px) {
        .stores-index__header {
          h2 {
            font: var(--dna-h4-font);
          }
        }
        .stores-index__store {
          h3 {
            font: var(--dna-h6-font);
          }
        }
      }

      @media screen and (min-width: 1024px) {
        .stores-index {
          margin-bottom: 0;
          padding: var(--dna-space-10) 0 calc(var(--dna-space-10) + var(--dna-space-1)) var(--dna-space-10);
        }
        .stores-index__header {
          padding-right: var(--dna-space-5);
          flex: 0 0 420px;
        }
        .stores-index__country-selector::part(nav) {
          margin-bottom: var(--dna-space-6);
          margin-right: var(--dna-space-10);
        }
        .stores-index__region::part(header) {
          margin-right: var(--dna-space-10);
        }
        .stores-index__list {
          gap: var(--dna-space-4);
          padding: var(--dna-space-6) 0 var(--dna-space-10);
          scroll-snap-type: none;
          scrollbar-color: var(--dna-color-light-grey) transparent;
        }
        .stores-index__list::-webkit-scrollbar-thumb {
          background: var(--dna-color-light-grey);
        }
        .stores-index__list::-webkit-scrollbar-track {
          background: transparent;
        }
        .stores-index__arrow {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          aspect-ratio: 1;
          background-color: rgba(255, 255, 255, 0.7);
          border-radius: 50%;
          color: var(--dna-brand-primary);
          cursor: pointer;
          width: var(--dna-space-10);
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
        }
        .stores-index__arrow[disabled] {
          display: none;
        }
        .stores-index__arrow--previous {
          left: var(--dna-space-10);
        }
        .stores-index__arrow--next {
          right: var(--dna-space-10);
        }
        .stores-index__store {
          flex: 0 0 310px;
        }
      }
    `,
  ]
}
customElements.define('dna-store-index', DNAStoreIndex)
